import { useMemo, useState } from 'react'
import {
  CloseButton,
  Button,
  Input,
  Popover,
  VStack,
  Text,
  Drawer,
  useDisclosure,
  Separator,
  Portal,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query'
import { FiPlus } from 'react-icons/fi'
import { useAssignTag } from '../../hooks/useContactDetail'
import { useTags, useCreateTag } from '../../hooks/useTag'
import { useIsMobile } from '../../hooks/useIsMobile'

export function ConductTagQuickAdd({ contactId, eventId }: { contactId: string; eventId: string }) {
  const isMobile = useIsMobile()
  const { data: tags } = useTags()
  const assignTag = useAssignTag(contactId)
  const createTag = useCreateTag()
  const qc = useQueryClient()
  const [q, setQ] = useState('')
  const disclosure = useDisclosure()

  const resolveByLabel = (label: string) =>
    (tags ?? []).find((t) => t.label.toLowerCase() === label.toLowerCase())

  const assignAndRefresh = (id: string) => {
    assignTag.mutate(id, {
      onSuccess: () => qc.invalidateQueries({ queryKey: ['lobby-day', 'attendees', eventId] }),
    })
  }

  const assignDoNotTap = async () => {
    const existing = resolveByLabel('Do Not Tap')
    const id = existing?.id ?? (await createTag.mutateAsync({ label: 'Do Not Tap', tagType: 'Conduct' })).id
    assignAndRefresh(id)
    disclosure.onClose()
  }

  const assignExisting = (id: string) => {
    assignAndRefresh(id)
    disclosure.onClose()
  }

  const createAndAssign = async (label: string) => {
    const created = await createTag.mutateAsync({ label, tagType: 'Conduct' })
    assignAndRefresh(created.id)
    disclosure.onClose()
  }

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase()
    // Only surface search results once the user starts typing — an empty query would
    // otherwise re-list "Do Not Tap" alongside the dedicated shortcut button above.
    if (!query) return []
    return (tags ?? []).filter((t) => t.label.toLowerCase().includes(query)).slice(0, 8)
  }, [tags, q])

  // ≥44px touch targets on mobile; desktop popover rows can stay compact.
  const touch = isMobile ? { minH: '44px' } : {}

  const body = (
    <VStack align="stretch" gap={2}>
      <Button size="sm" colorPalette="orange" variant="outline" onClick={assignDoNotTap} {...touch}>
        Do Not Tap
      </Button>
      <Separator />
      <Input size="sm" placeholder="Find a tag…" value={q} onChange={(e) => setQ(e.target.value)} {...touch} />
      {filtered.map((t) => (
        <Button key={t.id} size="sm" variant="ghost" justifyContent="flex-start"
          onClick={() => assignExisting(t.id)} {...touch}>
          {t.label}
        </Button>
      ))}
      {q.trim() && !resolveByLabel(q.trim()) && (
        <Button
          size="sm"
          variant="ghost"
          colorPalette="brand"
          onClick={() => createAndAssign(q.trim())}
          {...touch}><FiPlus />Create "{q.trim()}"
                  </Button>
      )}
      {(tags ?? []).length === 0 && <Text fontSize="xs" color="gray.500">No tags yet.</Text>}
    </VStack>
  )

  if (isMobile) {
    // Bottom-sheet on mobile — a Popover clips off-screen at 390px.
    return (
      <>
        <Button
          size="xs"
          variant="ghost"
          aria-label="Add tag"
          minH="44px"
          minW="44px"
          onClick={disclosure.onOpen}><FiPlus />Tag
                  </Button>
        <Drawer.Root open={disclosure.open} placement='bottom' onOpenChange={e => {
          if (!e.open) {
            disclosure.onClose();
          }
        }}>
          <Portal>

            <Drawer.Backdrop />
            <Drawer.Positioner>
              <Drawer.Content borderTopRadius="xl" maxH="85vh" pb="env(safe-area-inset-bottom, 0px)">
                <Drawer.CloseTrigger asChild><CloseButton size="sm" /></Drawer.CloseTrigger>
                <Drawer.Header borderBottomWidth="1px">Add tag</Drawer.Header>
                <Drawer.Body py={3}>{body}</Drawer.Body>
              </Drawer.Content>
            </Drawer.Positioner>

          </Portal>
        </Drawer.Root>
      </>
    );
  }

  return (
    <Popover.Root
      open={disclosure.open}
      onOpenChange={e => {
        if (e.open) {
          disclosure.onOpen();
        } else {
          disclosure.onClose();
        }
      }}
      positioning={{
        placement: 'bottom-end'
      }}>
      <Popover.Trigger asChild>
        <Button size="xs" variant="ghost" aria-label="Add tag"><FiPlus />Tag</Button>
      </Popover.Trigger>
      <Popover.Positioner>
        <Popover.Content w="260px">
          <Popover.Body>{body}</Popover.Body>
        </Popover.Content>
      </Popover.Positioner>
    </Popover.Root>
  );
}
